import * as stylex from "@stylexjs/stylex";
import { spacing } from "@softmax/design-system/tokens.stylex";
import { StoreLink } from "./store-link";

type StoreExternalLink = { readonly title: string; readonly href: string };

const linkOrder = [
  "Source",
  "README",
  "Rules",
  "Wiki",
  "Forum",
  "Docs",
  "Discord",
  "Agent guide (play.md)",
];

const bareLabel = (title: string) => title.replace(/\s*↗\s*$/, "").trim();

export function orderedStoreLinks(
  links: readonly StoreExternalLink[],
): StoreExternalLink[] {
  const byLabel = new Map(links.map((link) => [bareLabel(link.title), link]));
  return linkOrder
    .map((label) => byLabel.get(label))
    .filter((link): link is StoreExternalLink => Boolean(link));
}

export function StoreLinkList({
  links,
}: {
  readonly links: readonly StoreExternalLink[];
}) {
  return (
    <ul {...stylex.props(styles.links)}>
      {links.map((link) => {
        const external = link.href.startsWith("http");
        return (
          <li key={link.title}>
            <StoreLink
              href={link.href}
              target={external ? "_blank" : undefined}
              rel={external ? "noopener noreferrer" : undefined}
            >
              <span {...stylex.props(styles.external)}>
                {bareLabel(link.title)}
                {external ? (
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    {...stylex.props(styles.glyph)}
                  >
                    <line x1="7" y1="17" x2="17" y2="7" />
                    <polyline points="8 7 17 7 17 16" />
                  </svg>
                ) : null}
              </span>
            </StoreLink>
          </li>
        );
      })}
    </ul>
  );
}

const styles = stylex.create({
  links: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.space6,
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  external: {
    display: "inline-flex",
    alignItems: "center",
    gap: spacing.space4,
  },
  glyph: { display: "inline", flexShrink: 0 },
});
